import { Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { useMemo, useState, useRef, useEffect } from "react";
import { getFormattedFieldValue } from "../../utils";
import type { Section } from "../../types";
import type { ProccesedExpedientValues } from "@/types/entities/templates";
import dayjs from "dayjs";

const BADGE_GAP = 8;
const BUTTON_WIDTH = 70;
const MAX_VALUE_WIDTH = 300;
const MAX_LINES = 3;

function estimateBadgeWidth(
  field: { id: string; name?: string; configuration?: { type?: string } | null },
  expirations: Record<string, string>,
  processedValues: ProccesedExpedientValues,
): number {
  const fieldName = field.name || "";
  const fieldValue = processedValues[field.id]?.value
    ? getFormattedFieldValue(
        field.configuration?.type,
        processedValues[field.id].value,
        "No asignado",
      ).join(", ")
    : "No asignado";

  const labelWidth = fieldName.length * 8;
  const valueWidth = Math.min(fieldValue.length * 8, MAX_VALUE_WIDTH);
  const expirationIcon = expirations[field.id] ? 24 : 0;

  return labelWidth + valueWidth + 24 + 16 + expirationIcon;
}

function calculateVisibleBadges(
  badges: { id: string; name?: string; configuration?: { type?: string } | null }[],
  expirations: Record<string, string>,
  processedValues: ProccesedExpedientValues,
  containerWidth: number,
): number {
  let currentLineWidth = 0;
  let currentLine = 1;
  let count = 0;

  for (const field of badges) {
    const width = estimateBadgeWidth(field, expirations, processedValues);
    const gapWidth = count > 0 ? BADGE_GAP : 0;

    if (currentLineWidth + width + gapWidth + BUTTON_WIDTH > containerWidth) {
      currentLine++;
      currentLineWidth = width;
      if (currentLine > MAX_LINES) break;
    } else {
      currentLineWidth += width + gapWidth;
    }

    count++;
  }

  return count;
}

interface BadgesSectionProps {
  sections: Section[];
  expirations: Record<string, string>;
  processedValues: ProccesedExpedientValues;
  onOpenModal: () => void;
}

export const BadgesSection = ({
  sections,
  expirations,
  processedValues,
  onOpenModal,
}: BadgesSectionProps) => {
  const [visibleBadgesCount, setVisibleBadgesCount] = useState<number>(0);
  const badgesContainerRef = useRef<HTMLDivElement>(null);

  const allBadges = useMemo(() => {
    return (
      sections
        //!!!!!!!!! hide "relaciones" section !!!!!!!!!
        ?.filter(
          (section) => section.title.toLowerCase().trim() !== "relaciones",
        )
        ?.flatMap((section) => section.fields || []) || []
    );
  }, [sections]);

  useEffect(() => {
    if (!badgesContainerRef.current || allBadges.length === 0) return;

    const count = calculateVisibleBadges(
      allBadges,
      expirations,
      processedValues,
      badgesContainerRef.current.offsetWidth,
    );
    setVisibleBadgesCount(count);
  }, [allBadges, expirations, processedValues]);

  return (
    <div
      ref={badgesContainerRef}
      className="flex gap-2 flex-wrap [--badge-height:--spacing(7)] [--badge-py:--spacing(0.5)]"
    >
      {allBadges.slice(0, visibleBadgesCount).map((field) => (
        <div key={field.id} className="flex">
          {expirations[field.id] &&
            dayjs(expirations[field.id]).isValid() &&
            dayjs().isBefore(dayjs(expirations[field.id])) && (
              <Badge
                variant="warning"
                style={{
                  borderRadius: "6px 0 0 6px",
                }}
              >
                <Icon type="far" name="clock" />
              </Badge>
            )}
          <Badge
            className="h-(--badge-height) py-(--badge-py)"
            style={
              expirations[field.id] &&
              dayjs(expirations[field.id]).isValid() &&
              dayjs().isBefore(dayjs(expirations[field.id]))
                ? {
                    borderRadius: "0 6px 6px 0",
                    borderLeft: "0",
                  }
                : {}
            }
          >
            {field.name}:{" "}
            {processedValues[field.id]?.value &&
            processedValues[field.id].value.length > 0 ? (
              <span className="text-body-loud max-w-75 truncate">
                {getFormattedFieldValue(
                  field.configuration?.type,
                  processedValues[field.id].value,
                  "No asignado",
                ).join(", ")}
              </span>
            ) : (
              "No asignado"
            )}
          </Badge>
        </div>
      ))}
      <Button
        variant="tertiary"
        size="xs"
        onClick={onOpenModal}
        className="h-(--badge-height) py-(--badge-py)"
      >
        Ver más
      </Button>
    </div>
  );
};
